import React, { forwardRef, KeyboardEvent, useEffect, useRef, useState, useMemo } from 'react';

import DatePicker, { registerLocale, setDefaultLocale } from 'react-datepicker';
import { add, addBusinessDays, format, getDay, getYear, isValid, parse, parseISO, setYear } from 'date-fns';
import getValueFromEvent from '../connectedForm/utils/getValue';
import { Maybe } from '../../graphql/schema/p4types';

import enGB from 'date-fns/locale/en-GB';
import { CustomKeydownInput } from './CustomKeydownInput';

registerLocale('en-GB', enGB);
setDefaultLocale('en-GB');

type IDateInputEditorProps = {
  column: { options: { entityType: string; entityId: number; entityDisplay: unknown }; key: string };
  value: any;
  onCommit: (_: unknown, options: { value: any; skipTabBehaviour: boolean; setInactive: boolean }) => void;
  updateCellState: (options: { modalOpen: boolean }) => void;
  excludeDays?: Array<number>;
  minDate?: Date;
};

const DateInputEditor = forwardRef<any, IDateInputEditorProps>((props, ref) => {
  const { column, value, onCommit, updateCellState } = props;
  const { options, key } = column;

  const initialDate = parseISO(value);
  const [date, setDate] = useState(isValid(initialDate) ? initialDate : new Date());
  const [inputString, setInputString] = useState('');

  useEffect(() => {
    if (isValid(initialDate)) {
      setDate(initialDate);
      setInputString(format(initialDate, 'do MMM yyyy'));
    }
  }, [value]);

  const inputNodeRef = useRef();

  //Generate a fake 'component' to pass to EditorContainer.ts so we don't have to alter it too much to cope with functional components.
  const editorRef = useRef<{
    ignore: boolean;
    getValue: () => any;
    getInputNode: () => any;
    getEditorProps: () => { managedEditor: boolean };
  }>({} as any);
  (ref as (el: any) => void)(editorRef.current);
  //Pass some mocks to make EditorContainer.ts play nicely.
  editorRef.current.ignore = true;
  editorRef.current.getValue = () => getValue(date);
  editorRef.current.getInputNode = () => inputNodeRef.current;
  editorRef.current.getEditorProps = () => {
    return { managedEditor: true };
  };

  const getValue = (newValue: Date, keyCode?: Maybe<number>) => {
    const updated: any = {};
    const updatedValue = format(newValue, 'yyyy-MM-dd');
    updated[key] = updatedValue;

    const { entityType, entityId, entityDisplay } = options;
    updated.entityType = entityType;
    updated.idPath = entityId;
    updated.displayPath = entityDisplay;
    updated.idValue = updatedValue;
    updated.lastKeyCode = keyCode;

    return updated;
  };

  const onSelectDate = (newDate: Date, event: MouseEvent) => {
    setDate(newDate);
    if (event.type === 'click') {
      onCommit({}, { value: getValue(newDate, null), skipTabBehaviour: false, setInactive: true });
    }
    updateCellState({ modalOpen: false });
  };

  const excludeDays = props?.excludeDays ?? [];

  const filterDate = useMemo(() => {
    return excludeDays.length === 0 && !props.minDate
      ? undefined
      : (date: Date) => {
          const afterMinDate = props.minDate ? date >= props.minDate : true;
          const notExcludedDay = excludeDays.length === 0 || !excludeDays.includes(getDay(date));
          return afterMinDate && notExcludedDay;
        };
  }, [excludeDays, props.minDate]);

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>, baseOnKeyDown: Function) => {
    switch (event.keyCode) {
      case 9: //TAB
      case 13: //ENTER
      case 37: //ARROW LEFT
      case 38: //ARROW UP
      case 39: //ARROW RIGHT
      case 40: //ARROW DOWN
        let newDate = date;
        let potentialDate;

        const dateString = getValueFromEvent(event);

        //If user enters a 'day/month' date - Coerce it to standard format.
        if (
          isValid((potentialDate = parse(dateString, 'dd/MM', new Date()))) ||
          isValid((potentialDate = parse(dateString, 'dd/MM/yy', new Date()))) ||
          isValid((potentialDate = parse(dateString, 'dd/MM/yyyy', new Date()))) ||
          isValid((potentialDate = parse(dateString, 'do MMM', new Date()))) ||
          isValid((potentialDate = parse(dateString, 'do MMM yy', new Date()))) ||
          isValid((potentialDate = parse(dateString, 'do MMM yyyy', new Date())))
        ) {
          newDate = potentialDate;
        } else if (inputString.toLowerCase() === 't') {
          newDate = new Date();
        } else if (/^([+-])\d{1,6}([dwm]){0,1}$/i.test(inputString.trim())) {
          const units = inputString.slice(-1).toLowerCase();
          let off: number;
          if (units === 'm') {
            off = parseInt(inputString.slice(0, -1), 10);
            //Go one day before the target, then add one *business* day so we skip over weekend as appropriate.
            newDate = add(new Date(), { months: off, days: -1 });
            newDate = addBusinessDays(newDate, 1);
          } else if (units === 'w') {
            off = parseInt(inputString.slice(0, -1), 10);
            newDate = add(new Date(), { weeks: off });
          } else {
            const offString = units === 'd' ? inputString.slice(0, -1) : inputString;
            newDate = add(new Date(), { days: parseInt(offString, 10) });
          }
        } else if (newDate.getFullYear() === 2001) {
          //If user enters only day/month, the year defaults to 2001 - But we want it to use the current year.
          newDate = setYear(newDate, getYear(new Date()));
        }

        if (isValid(newDate)) {
          onCommit({}, { value: getValue(newDate, event.keyCode), skipTabBehaviour: false, setInactive: true });
        }
        event.preventDefault();
        break;
      default:
        baseOnKeyDown(event);
    }
  };

  const onChangeRaw = (event) => {
    const value = getValueFromEvent(event);
    setInputString(value);
  };

  return (
    <DatePicker
      dateFormat="do MMM yyyy"
      selected={date}
      onChange={onSelectDate}
      onChangeRaw={onChangeRaw}
      autoFocus={true}
      customInput={<CustomKeydownInput ref={inputNodeRef} customOnKeydown={onKeyDown} inGrid={true} />}
      locale={'en-GB'}
      portalId={'root-portal'}
      filterDate={filterDate}
    />
  );
});

export default DateInputEditor;
